import {
  BankIcon,
  BuildingIcon,
  CampaignFilledIcon,
  ChartIcon,
  ClockIcon,
  DevicesIcon,
  DirectionsFilledIcon,
  DocumentIcon,
  FolderFilledIcon,
  HomeIcon,
  MediaLibraryIcon,
  PhoneIcon,
  SettingFilledIcon,
  ShieldCheckFilledIcon,
  ShieldFilledIcon,
  SigmaIcon,
  StoreFilledIcon,
  TreeDiagramIcon,
  VoicemailIcon,
} from '@sharedCR/coreDesignSystem/icons';

import { PageCodeComparisonType } from 'constants/claims';
import { FeatureFlag } from 'constants/featureFlags';
import { ANY_HEADER_VIEW_PAGE_CODE } from 'constants/headerViewPermissions';
import { NAVIGATION_ITEM_TYPE } from 'constants/navigation';
import { PAGE_CODES } from 'constants/pageCodes';

import { marginCenterNavItem } from './marginCenterNav';
import { postClosingOptionsNavItems } from './postClosingOptionsNav';
import { AccountingPaths } from './routesConfig/accounting';
import { AnalyticsAdminPaths } from './routesConfig/analyticsAdmin';
import { AudioRecordingsPaths } from './routesConfig/audioRecordings';
import { BrokersPaths } from './routesConfig/brokers';
import { CapitalMarketsPaths } from './routesConfig/capitalMarkets';
import { ChatEscalationPaths } from './routesConfig/chatEscalation';
import { CompaniesPaths } from './routesConfig/companies';
import { CompanyPaths } from './routesConfig/company';
import { CompanyCenterPaths } from './routesConfig/companyCenter';
import { CompanyManagerPaths } from './routesConfig/companyManager';
import { CrmPaths } from './routesConfig/crm';
import { DashboardsPaths } from './routesConfig/dashboards';
import { DevToolsPaths } from './routesConfig/devTools';
import { DocumentsDataPaths } from './routesConfig/documentData';
import { DocumentsPaths } from './routesConfig/documents';
import { FormsPaths } from './routesConfig/forms';
import { LoanProgressMilestonesPaths } from './routesConfig/loanProgressMilestones';
import { PipelinesPaths } from './routesConfig/pipelines';
import { ProgramsPaths } from './routesConfig/programs';
import { RealtorsPaths } from './routesConfig/realtors';
import { ReportsPaths } from './routesConfig/reports';
import { SecondaryPaths } from './routesConfig/secondary';
import { SystemPaths } from './routesConfig/system';
import { TpoCertificationPaths } from './routesConfig/tpoCertification';
import { TpoPortalPaths } from './routesConfig/tpoPortal';
import { UnderwritingPaths } from './routesConfig/underwriting';
import { UsersPaths } from './routesConfig/users';
import { WorkSchedulerPaths } from './routesConfig/workScheduler';
import { underwritingNavItem } from './underwritingNav';

const navigationConfig = [
  {
    id: 'home',
    title: 'Home',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    icon: HomeIcon,
    url: TpoPortalPaths.commandCenter.path,
    exact: true,
    // The Command Center home is the default landing + first nav item for all
    // authenticated users (the page self-adapts broker vs company-level).
    skipPermissionCheck: true,
  },
  {
    id: 'tpo-certification',
    title: 'Certification',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    icon: ShieldCheckFilledIcon,
    url: TpoCertificationPaths.tpoCertification.path,
    skipPermissionCheck: true,
    isCertificationPendingOnly: true,
  },
  {
    id: 'company-center',
    title: 'Company Center',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    icon: BuildingIcon,
    url: CompanyCenterPaths.companyCenter.path,
    exact: true,
    pageCode: PAGE_CODES.COMPANY_CENTER,
    isBrokerCompanyOnly: true,
  },
  {
    id: 'company-manager',
    title: 'My Company',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    icon: StoreFilledIcon,
    url: CompanyManagerPaths.companyManager.path,
    exact: true,
    pageCode: PAGE_CODES.COMPANY_MANAGER,
    isBrokerCompanyOnly: true,
  },
  {
    id: 'dashboards',
    title: 'Dashboards',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    url: DashboardsPaths.dashboards.path,
    pageCode: PAGE_CODES.DASHBOARDS,
  },
  {
    id: 'reports',
    title: 'Reports',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    url: ReportsPaths.reports.path,
    pageCode: PAGE_CODES.REPORT_BUILDERS_VIEW,
  },
  {
    id: 'pipelines',
    title: 'Pipelines',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    url: PipelinesPaths.loanPipelines.path,
    exact: true,
    pageCode: PAGE_CODES.PIPELINE_LOAN_VIEW,
  },
  {
    // Own nav area, deliberately separate from the technical "Automated Actions" designer.
    // Gated on crm.campaigns (DRIP_CAMPAIGNS) — the claim is defined (Accounts migration #986,
    // granted on dev), so the nav item is hidden from admins who lack it.
    id: 'drip-campaigns',
    title: 'Drip Campaigns',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    icon: CampaignFilledIcon,
    url: SystemPaths.dripCampaigns.path,
    exact: true,
    pageCode: PAGE_CODES.CRM_CAMPAIGNS,
  },
  {
    id: 'documentData',
    title: 'Document Extraction',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    url: DocumentsDataPaths.documentData.path,
    pageCode: PAGE_CODES.DOCUMENT_EXTRACTION,
  },
  {
    id: 'pricingWorkbench',
    title: 'Pricing Workbench',
    type: NAVIGATION_ITEM_TYPE.ITEM,
    url: ProgramsPaths.pricingWorkbench.path,
    exact: true,
    pageCode: PAGE_CODES.PRICING_WORKBENCH,
  },
  marginCenterNavItem,
  {
    id: 'applications-tab',
    title: 'My Settings',
    type: NAVIGATION_ITEM_TYPE.GROUP,
    icon: HomeIcon,
    children: [
      {
        id: 'work-scheduler',
        title: 'My Working Schedule',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: ClockIcon,
        url: WorkSchedulerPaths.workScheduler.path,
        pageCode: PAGE_CODES.WORK_SCHEDULER,
      },
      {
        id: 'realtors',
        title: 'Realtors',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: HomeIcon,
        url: RealtorsPaths.realtors.path,
        pageCode: PAGE_CODES.REALTORS,
      },
      {
        id: 'audio-recordings',
        title: 'Audio Recordings',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: VoicemailIcon,
        url: AudioRecordingsPaths.audioRecordings.path,
        pageCode: PAGE_CODES.AUDIO_RECORDINGS,
      },
    ],
  },
  {
    id: 'accounting-tab',
    title: 'Accounting',
    type: NAVIGATION_ITEM_TYPE.GROUP,
    icon: BankIcon,
    isHiddenOnMobile: true,
    children: [
      {
        id: 'advance',
        title: 'Warehouse Advance',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: AccountingPaths.advance.path,
        exact: true,
        pageCode: PAGE_CODES.ADVANCE,
      },
      {
        id: 'purchaseAdvice',
        title: 'Purchase Advice',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: AccountingPaths.purchaseAdvice.path,
        exact: true,
        pageCode: PAGE_CODES.PURCHASE_ADVICE,
      },
      {
        id: 'purchasedLoans',
        title: 'Purchased Loans',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: AccountingPaths.purchasedLoans.path,
        exact: true,
        pageCode: PAGE_CODES.PURCHASED_LOANS,
      },
      {
        id: 'accounting-settings',
        title: 'Accounting Settings',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: CompanyPaths.accountingSettings.path,
        pageCode: PAGE_CODES.ACCOUNTING_SETTINGS,
      },
      {
        id: 'csv-mappings',
        title: 'CSV Mapping',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: CompanyPaths.csvMappings.path,
        pageCode: PAGE_CODES.CSV_MAPPINGS,
      },
    ],
  },
  {
    id: 'system-tab',
    title: 'System',
    type: NAVIGATION_ITEM_TYPE.GROUP,
    icon: DevicesIcon,
    isHiddenOnMobile: true,
    children: [
      {
        id: 'analytics-admin-section',
        title: 'Analytics',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: ChartIcon,
        children: [
          {
            id: 'analytics-admin',
            title: 'Analytics Admin',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AnalyticsAdminPaths.analyticsAdmin.path,
            pageCode: PAGE_CODES.ANALYTICS_ADMIN,
          },
        ],
      },
      {
        id: 'forms-category',
        title: 'Form Configuration',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: MediaLibraryIcon,
        children: [
          {
            id: 'forms',
            title: 'Forms',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: FormsPaths.forms.path,
            pageCode: PAGE_CODES.FORMS,
          },
          {
            id: 'form-bundles',
            title: 'Form Bundles',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: FormsPaths.formBundle.path,
            pageCode: PAGE_CODES.FORM_BUNDLE,
          },
          {
            id: 'form-bundles-testing-tool',
            title: 'Form Bundle Tester',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: FormsPaths.formBundleTestingTool.path,
            pageCode: PAGE_CODES.FORM_BUNDLE_TESTING_TOOL,
          },
        ],
      },
      {
        id: 'marketing-category',
        title: 'Marketing',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: BuildingIcon,
        children: [
          {
            id: 'lead-sources',
            title: 'Lead Sources',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.leadSources.path,
            pageCode: PAGE_CODES.LEAD_SOURCES,
          },
        ],
      },
      {
        id: 'operations-category',
        title: 'Operations Configuration',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: TreeDiagramIcon,
        children: [
          {
            id: 'appraisal-products',
            title: 'Appraisal Products',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appraisalProducts.path,
            exact: true,
            pageCode: PAGE_CODES.APPRAISAL_PRODUCTS,
          },
          {
            id: 'homevision-job-types',
            title: 'HomeVision Job Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.homeVisionJobTypes.path,
            exact: true,
            pageCode: PAGE_CODES.HOMEVISION_JOB_TYPES,
          },
          {
            id: 'appraisal-product-criteria',
            title: 'Appraisal Product Criteria',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appraisalProductBuilder.path,
            pageCode: PAGE_CODES.APPRAISAL_PRODUCT_CRITERIA,
            FF: FeatureFlag.EnableAppraisalProductCriteria,
          },
          {
            id: 'appraisal-product-pricing',
            title: 'Appraisal Product Pricing',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appraisalProductPricing.path,
            exact: true,
            pageCode: PAGE_CODES.APPRAISAL_PRODUCT_PRICING,
          },
          {
            id: 'appraisal-surcharges',
            title: 'Appraisal Surcharges',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appraisalSurcharges.path,
            exact: true,
            pageCode: PAGE_CODES.APPRAISAL_SURCHARGES,
          },
          {
            id: 'appraisal-surcharge-criteria',
            title: 'Appraisal Surcharge Criteria',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appraisalSurchargeCriteria.path,
            exact: true,
            pageCode: PAGE_CODES.APPRAISAL_SURCHARGE_CRITERIA,
          },
          {
            id: 'checklists',
            title: 'Checklists',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.checklists.path,
            exact: true,
            pageCode: PAGE_CODES.CHECKLISTS,
          },
          {
            id: 'contact-types',
            title: 'Contact Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.contactTypes.path,
            exact: true,
            pageCode: PAGE_CODES.CONTACT_TYPES,
          },
          {
            id: 'denial-reasons',
            title: 'Denial Reasons',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.denialReasons.path,
            exact: true,
            pageCode: PAGE_CODES.DENIAL_REASONS,
          },
          {
            id: 'exception-sub-categories',
            title: 'Exception Sub-Categories',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.exceptionSubCategories.path,
            exact: true,
            pageCode: PAGE_CODES.EXCEPTION_SUB_CATEGORIES,
          },
          {
            id: 'exception-tracking-report',
            title: 'Exception Tracking Report',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.exceptionTrackingReport.path,
            exact: true,
            pageCode: PAGE_CODES.EXCEPTION_TRACKING_REPORT,
          },
          {
            id: 'cic-fee-reasons',
            title: 'CIC Reasons',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.cicFeeReasons.path,
            exact: true,
            pageCode: PAGE_CODES.CIC_FEE_REASONS,
          },
          {
            id: 'fee-providers',
            title: 'Fee Providers',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.feeProviders.path,
            exact: true,
            pageCode: {
              pageCodes: [PAGE_CODES.FEE_PROVIDER_CATEGORY, PAGE_CODES.FEE_PROVIDER],
              comparisonType: PageCodeComparisonType.OR,
            },
          },
          {
            id: 'fee-types',
            title: 'Fee Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.feeTypes.path,
            exact: true,
            pageCode: PAGE_CODES.FEE_TYPES,
          },
          {
            id: 'impound-types',
            title: 'Impound Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.impoundTypes.path,
            exact: true,
            pageCode: PAGE_CODES.FEE_TYPES,
          },
          ...postClosingOptionsNavItems,
          {
            id: 'seller-adjustment-types',
            title: 'Seller Adjustment Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.sellerAdjustmentTypes.path,
            exact: true,
            pageCode: PAGE_CODES.SELLER_ADJUSTMENT_TYPES,
          },
          {
            id: 'borrower-vesting-types',
            title: 'Borrower Vesting',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.borrowerVestingTypes.path,
            exact: true,
            pageCode: PAGE_CODES.VESTING_TYPE,
          },
          {
            id: 'documentBundles',
            title: 'Document Bundle Configuration',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.documentBundles.path,
            exact: true,
            pageCode: PAGE_CODES.DOCUMENT_BUNDLE_TYPE,
          },
        ],
      },
      {
        id: 'pos-category',
        title: 'POS',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: StoreFilledIcon,
        children: [
          {
            id: 'apply-form-custom-questions',
            title: 'Custom Questions',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.applyFormCustomQuestions.path,
            pageCode: PAGE_CODES.APPLY_FORM_CUSTOM_QUESTIONS,
          },
          {
            id: 'pos-configurations',
            title: 'POS Configurations',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.posConfigurations.path,
            pageCode: PAGE_CODES.POS_CONFIGURATIONS,
          },
          {
            id: 'advisor-portal-configurations',
            title: 'Advisor Portal Settings',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.advisorPortalConfigurations.path,
            pageCode: PAGE_CODES.ADVISOR_PORTAL_CONFIGURATIONS,
          },
          {
            id: 'taskCategories',
            title: 'Task Categories',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.taskCategories.path,
            exact: true,
            pageCode: PAGE_CODES.TASK_CATEGORIES,
          },
        ],
      },
      {
        id: 'tpo-category',
        title: 'TPO',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: BankIcon,
        children: [
          {
            id: 'tpo-signup-configurations',
            title: 'TPO Signup Configurations',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.tpoSignupConfigurations.path,
            pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
          },
          {
            id: 'tpo-status-configuration',
            title: 'TPO Status Configuration',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.tpoStatusConfiguration.path,
            pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
          },
          {
            id: 'tpo-denial-configuration',
            title: 'Denial Configuration',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.tpoDenialConfiguration.generatePath(),
            pageCode: PAGE_CODES.TPO_SIGNUP_CONFIGURATIONS,
          },
        ],
      },
      {
        id: 'secondary-category',
        title: 'Secondary',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: SigmaIcon,
        children: [
          {
            id: 'trades',
            title: 'Trades',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CapitalMarketsPaths.trades.path,
            exact: true,
            pageCode: PAGE_CODES.TRADE_MANAGEMENT,
          },
          {
            id: 'dealers',
            title: 'Dealers',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AccountingPaths.dealers.path,
            exact: true,
            pageCode: PAGE_CODES.DEALERS,
          },
          {
            id: 'investors',
            title: 'Investors',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AccountingPaths.investors.path,
            exact: true,
            pageCode: PAGE_CODES.INVESTORS,
          },
          {
            id: 'investor-feature-codes',
            title: 'Investor Feature Codes',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.investorFeatureCodes.path,
            exact: true,
            pageCode: PAGE_CODES.INVESTOR_FEATURE_CODES,
          },
          {
            id: 'program-identifiers',
            title: 'Program Identifiers',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.programIdentifiers.path,
            exact: true,
            pageCode: PAGE_CODES.PROGRAM_IDENTIFIERS,
          },
          {
            id: 'servicing-companies',
            title: 'Servicing Companies',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AccountingPaths.servicingCompanies.path,
            exact: true,
            pageCode: PAGE_CODES.SERVICING_COMPANY,
          },
          {
            id: 'warehouses',
            title: 'Warehouse Banks',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AccountingPaths.warehouses.path,
            exact: true,
            pageCode: PAGE_CODES.WAREHOUSE_BANKS,
          },
          {
            id: 'optimization-strategies',
            title: 'Optimization Strategies',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: AccountingPaths.optimizationStrategies.path,
            exact: true,
            pageCode: PAGE_CODES.WAREHOUSE_BANKS,
          },
          {
            id: 'adjustments',
            title: 'Adjustments',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.adjustments.path,
            exact: true,
            pageCode: PAGE_CODES.ADJUSTMENTS,
          },
          {
            id: 'customAdjustments',
            title: 'Custom Adjustments',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.customAdjustments.path,
            exact: true,
            pageCode: PAGE_CODES.CUSTOM_ADJUSTMENTS,
          },
          {
            id: 'eligibilityBuilder',
            title: 'Eligibility Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.eligibilityBuilder.path,
            exact: true,
            pageCode: PAGE_CODES.ELIGIBILITY_BUILDER,
          },
          {
            id: 'escrowRequirements',
            title: 'Escrow Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.escrowRequirements.path,
            exact: true,
            pageCode: PAGE_CODES.ESCROW_REQUIREMENTS,
          },
          {
            id: 'llpa-caps',
            title: 'Adjustment Caps',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.llpaCaps.path,
            exact: true,
            pageCode: PAGE_CODES.LLPA_CAP,
          },
          {
            id: 'llpa-types',
            title: 'Adjustment Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.llpaTypes.path,
            exact: true,
            pageCode: PAGE_CODES.LLPA_TYPE,
          },
          {
            id: 'mi-coverage',
            title: 'MI Coverage Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.miCoveragePercentage.path,
            exact: true,
            pageCode: PAGE_CODES.MI_COVERAGE_CRITERIA,
            FF: FeatureFlag.MiCoveragePercentages,
          },
          {
            id: 'mi-rules',
            title: 'MI Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.miRules.path,
            exact: true,
            pageCode: PAGE_CODES.MI_RULES,
          },
          {
            id: 'rate-lock-terms',
            title: 'Rate Lock Terms',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.rateLockTerms.path,
            exact: true,
            pageCode: PAGE_CODES.RATE_LOCK_TERMS,
          },
          {
            id: 'buyDowns',
            title: 'Buy Downs',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ProgramsPaths.buyDowns.path,
            exact: true,
            pageCode: PAGE_CODES.BUY_DOWNS,
          },
          {
            id: 'msr',
            title: 'MSR Valuations',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SecondaryPaths.msr.path,
            pageCode: PAGE_CODES.MSR,
          },
        ],
      },
      {
        id: 'security-category',
        title: 'Security',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: ShieldFilledIcon,
        children: [
          {
            id: 'roles',
            title: 'Security Groups',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.roles.path,
            exact: true,
            pageCode: PAGE_CODES.ROLES,
          },
          {
            id: 'users',
            title: 'Users',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: UsersPaths.users.path,
            pageCode: PAGE_CODES.USERS,
          },
          {
            id: 'app-keys',
            title: 'App Keys',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.appKeys.path,
            exact: true,
            pageCode: {
              pageCodes: [PAGE_CODES.COMPANY_APP_KEYS, PAGE_CODES.INTERNAL_COMPANY_APP_KEYS],
              comparisonType: PageCodeComparisonType.OR,
            },
          },
        ],
      },
      {
        id: 'system-category',
        title: 'System',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: SettingFilledIcon,
        children: [
          {
            id: 'company-audio-recordings',
            title: 'Audio Recordings',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.companyAudioRecordings.path,
            pageCode: PAGE_CODES.COMPANY_AUDIO_RECORDINGS,
          },
          {
            id: 'company',
            title: 'Company',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.company.path,
            pageCode: PAGE_CODES.COMPANY,
          },
          {
            id: 'emailNotifications',
            title: 'Email Templates',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.emailTemplates.path,
            exact: true,
            pageCode: PAGE_CODES.EMAIL_TEMPLATE,
          },
          {
            id: 'header-views',
            title: 'Header views',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.headerViews.path,
            exact: true,
            pageCode: ANY_HEADER_VIEW_PAGE_CODE,
            FF: FeatureFlag.HideLoanHeaderViews,
            // TODO: rename this FF to get rid of isFFNegative
            isFFNegative: true,
          },
          {
            id: 'command-center-layout',
            title: 'Command Center layout',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.commandCenterLayoutBuilder.path,
            exact: true,
            pageCode: PAGE_CODES.HEADER_VIEW_LOAN,
          },
          {
            id: 'command-center-announcements',
            title: 'Command Center announcements',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.commandCenterAnnouncements.path,
            exact: true,
            pageCode: PAGE_CODES.COMMAND_CENTER_MANAGE_ANNOUNCEMENTS,
          },
          {
            id: 'command-center-client-protection',
            title: 'Command Center client protection',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.commandCenterClientProtection.path,
            exact: true,
            pageCode: PAGE_CODES.SYSTEM_PROPERTIES,
          },
          {
            id: 'command-center-quality',
            title: 'Command Center quality (coaching)',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.commandCenterQuality.path,
            exact: true,
            pageCode: PAGE_CODES.SYSTEM_PROPERTIES,
          },
        ],
      },
      underwritingNavItem,
      {
        id: 'crm-category',
        title: 'CRM',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: FolderFilledIcon,
        FF: FeatureFlag.EnableCrm,
        isCrmCompanyGated: true,
        children: [
          {
            id: 'crm-contacts',
            title: 'Contacts',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CrmPaths.contactList.path,
            exact: true,
            pageCode: PAGE_CODES.CRM_CONTACTS,
          },
          {
            id: 'crm-tags',
            title: 'CRM Tags',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CrmPaths.crmTags.path,
            exact: true,
            pageCode: PAGE_CODES.CRM_TAGS,
          },
          {
            id: 'crm-lead-assignment-rules',
            title: 'Lead Assignment Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CrmPaths.leadAssignmentRules.path,
            exact: true,
            pageCode: PAGE_CODES.USERS_LEAD_ASSIGNMENT,
          },
        ],
      },
      {
        id: 'workflow-category',
        title: 'Workflow',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: DirectionsFilledIcon,
        children: [
          {
            id: 'ai-chat-configurations',
            title: 'AI Chat Configurations',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiChatConfigurations.path,
            exact: true,
            pageCode: PAGE_CODES.AI_CHAT_CONFIGURATIONS,
          },
          {
            id: 'ai-personas',
            title: 'AI Personas',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiPersonas.path,
            exact: true,
            pageCode: PAGE_CODES.AI_PERSONAS,
          },
          {
            id: 'ai-processes',
            title: 'AI Processes',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiProcesses.path,
            exact: true,
            pageCode: PAGE_CODES.AI_PROCESSES,
          },
          {
            id: 'ai-rule-libraries',
            title: 'AI Rule Libraries',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiRuleLibraries.path,
            exact: true,
            pageCode: PAGE_CODES.AI_PROCESSES,
          },
          {
            id: 'ai-guidelines',
            title: 'AI Guidelines',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.guidelines.path,
            exact: true,
            pageCode: PAGE_CODES.GUIDELINES,
          },
          {
            id: 'ai-knowledge',
            title: 'AI Knowledge',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.knowledgeItems.path,
            exact: true,
            pageCode: PAGE_CODES.KNOWLEDGE,
          },
          {
            id: 'ai-agents',
            title: 'AI Agents',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiAgents.path,
            exact: true,
            pageCode: PAGE_CODES.AI_AGENTS,
          },
          {
            id: 'ai-skills',
            title: 'AI Skills',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiSkills.path,
            exact: true,
            pageCode: PAGE_CODES.AI_SKILLS,
          },
          {
            id: 'ai-usage',
            title: 'AI Usage',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.aiUsage.generatePath(),
            exact: false,
            pageCode: PAGE_CODES.AI_LOG,
          },
          {
            id: 'auto-lock-rules',
            title: 'Auto-lock Rules',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.autoLockRulesBuilder.path,
            pageCode: PAGE_CODES.AUTO_LOCK_RULES_MANAGEMENT,
          },
          {
            id: 'automated-action-criteria',
            title: 'Automated Actions',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.automatedActionCriteriaBuilder.path,
            pageCode: PAGE_CODES.AUTOMATED_ACTION_CRITERIA,
          },
          {
            id: 'documentExtractionFieldsMapping',
            title: 'Document Extraction Mapping',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.documentExtractionFieldsMapping.path,
            exact: true,
            pageCode: PAGE_CODES.DOCUMENT_EXTRACTION_FIELDS_MAPPING,
          },
          {
            id: 'loan-actions',
            title: 'Loan Actions',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.loanActionsBuilder.path,
            exact: false,
            pageCode: PAGE_CODES.LOAN_ACTIONS,
          },
          {
            id: 'integration-studio',
            title: 'Integration Studio',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.integrationStudioBase.path,
            exact: false,
            pageCode: PAGE_CODES.INTEGRATION_STUDIO,
          },
          {
            id: 'loan-progress-milestones',
            title: 'Loan Progress Milestones',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: LoanProgressMilestonesPaths.loanProgressMilestones.path,
            pageCode: PAGE_CODES.MANAGE_LOAN_FLOW_MILESTONES,
          },
          {
            id: 'loan-roles',
            title: 'Loan Roles',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.loanRoles.path,
            exact: true,
            pageCode: PAGE_CODES.LOAN_ROLE,
          },
          {
            id: 'loan-role-relationships',
            title: 'Loan Role Relationships',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.loanRoleRelationships.path,
            exact: true,
            pageCode: PAGE_CODES.LOAN_ROLE_RELATIONSHIPS,
          },
          {
            id: 'chat-escalation',
            title: 'Chat Escalation',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: ChatEscalationPaths.chatEscalation.path,
            exact: true,
            pageCode: PAGE_CODES.CHAT_ESCALATION_LADDER,
          },
          {
            id: 'pipelines-configuration',
            title: 'Pipelines Configuration',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            icon: DocumentIcon,
            url: SystemPaths.pipelinesBuilder.path,
            exact: true,
            pageCode: PAGE_CODES.PIPELINES,
          },
          {
            id: 'tags',
            title: 'Loan Tags',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.tags.path,
            exact: true,
            pageCode: PAGE_CODES.TAGS,
          },
          {
            id: 'turn-time-metrics',
            title: 'Turn-Time Metrics',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.turnTimeDefinitions.path,
            exact: true,
            pageCode: PAGE_CODES.TAGS,
          },
          {
            id: 'sla-groups',
            title: 'SLA Groups',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: UnderwritingPaths.slaGroups.path,
            pageCode: PAGE_CODES.UNDERWRITING_SLA_GROUPS,
          },
        ],
      },
      {
        id: 'telephony-category',
        title: 'Telephony',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: PhoneIcon,
        children: [
          {
            id: 'texml-applications',
            title: 'TeXML Applications',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.texmlApplications.path,
            exact: true,
            pageCode: PAGE_CODES.TEXML_APPLICATIONS,
          },
        ],
      },
      {
        id: 'other-category',
        title: 'Other',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: DevicesIcon,
        children: [
          {
            id: 'active-applicants',
            title: 'Active Applicants',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.activeApplicants.path,
            pageCode: PAGE_CODES.APPLICATION,
          },
          {
            id: 'brokers',
            title: 'Brokers',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: BrokersPaths.brokers.path,
            pageCode: PAGE_CODES.BROKERS,
          },
          {
            id: 'loan-officers-management',
            title: 'LO Management',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: CompanyPaths.loanOfficersManagement.path,
            pageCode: PAGE_CODES.LOAN_OFFICERS_MANAGEMENT,
          },
          {
            id: 'documents',
            title: 'Documents',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: DocumentsPaths.documents.path,
            pageCode: PAGE_CODES.LOAN_DOCUMENT,
          },
        ],
      },
      {
        id: 'companies',
        title: 'Companies',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: CompaniesPaths.companies.path,
        pageCode: PAGE_CODES.INTERNAL_COMPANIES,
      },
      {
        id: 'system',
        title: 'System',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: FolderFilledIcon,
        url: SystemPaths.system.path,
        children: [
          {
            id: 'analytics',
            title: 'Analytics',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.analytics.path,
            exact: true,
            FF: FeatureFlag.EnableCubePlayground,
          },
          {
            id: 'documentFieldTypes',
            title: 'Document Field Types',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.documentFieldTypes.path,
            exact: true,
            pageCode: PAGE_CODES.INTERNAL_DOCUMENT_FIELD_TYPE,
          },
          {
            id: 'categoryManager',
            title: 'Category Manager',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.categoryManager.path,
            exact: true,
            pageCode: PAGE_CODES.INTERNAL_DOCUMENT_CATEGORY,
          },
          {
            id: 'system-settings',
            title: 'System settings',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            icon: FolderFilledIcon,
            url: SystemPaths.systemSettings.path,
            pageCode: PAGE_CODES.INTERNAL_SYSTEM_SETTINGS,
          },
          {
            id: 'property-definitions',
            title: 'Property Definitions',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.propertyDefinitions.path,
            exact: true,
            pageCode: PAGE_CODES.INTERNAL_COMPANIES_SYSTEM_PROPERTIES,
          },
          {
            id: 'webhooks',
            title: 'Webhooks',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.webhooks.path,
            exact: true,
            pageCode: PAGE_CODES.WEBHOOKS,
          },
          {
            id: 'notification-dispatcher',
            title: 'Notification Dispatcher',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SystemPaths.notificationDispatcher.path,
            exact: true,
            pageCode: PAGE_CODES.NOTIFICATION_DISPATCHER,
            FF: FeatureFlag.EnableNotificationDispatcher,
          },
        ],
      },
      {
        id: 'secondary-folder-category',
        title: 'Secondary',
        type: NAVIGATION_ITEM_TYPE.COLLAPSE,
        icon: FolderFilledIcon,
        children: [
          {
            id: 'market-to-market',
            title: 'Mark2Market Settings',
            type: NAVIGATION_ITEM_TYPE.ITEM,
            url: SecondaryPaths.marketToMarket.path,
            pageCode: PAGE_CODES.MARK_TO_MARKET,
          },
        ],
      },
      {
        id: 'customFieldDefinitions',
        title: 'Custom Field Definitions',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: SystemPaths.customFieldDefinitions.path,
        pageCode: PAGE_CODES.CUSTOM_FIELD_DEFINITIONS,
      },
      {
        id: 'contactCustomFieldDefinitions',
        title: 'Contact Custom Field Definitions',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: SystemPaths.contactCustomFieldDefinitions.path,
        pageCode: PAGE_CODES.CRM_CONTACTS,
      },
      {
        id: 'loanEditorCustomForms',
        title: 'Loan Editor Custom Forms',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        icon: FolderFilledIcon,
        url: SystemPaths.loanEditorCustomForms.path,
        exact: true,
        pageCode: PAGE_CODES.LOAN_EDITOR_CUSTOM_FORMS,
      },
    ],
  },
  {
    id: 'dev-tools',
    title: 'Dev Tools',
    type: NAVIGATION_ITEM_TYPE.COLLAPSE,
    icon: DevicesIcon,
    children: [
      {
        id: 'ob-logger',
        title: 'OB Logger',
        type: NAVIGATION_ITEM_TYPE.ITEM,
        url: DevToolsPaths.obLogger.path,
        pageCode: PAGE_CODES.INTERNAL_HTTP_REQUEST_LOG,
      },
    ],
  },
];

export default navigationConfig;
